import React, { useEffect, useMemo, useState } from 'react';
import { useSearchParams } from 'react-router-dom';
import { fetchWithAuth } from '../util/fetchWithAuth';
import { formatCurrency } from '../util/formatCurrency';

type MatrixRow = { label: string; values: number[] };
type RowGroup = Record<string, MatrixRow>;

type MatrixResponse = {
  months: string[];
  rows: {
    app: RowGroup;
    bank: {
      income: RowGroup;
      investment: MatrixRow;
      expenses: RowGroup;
      profit: RowGroup;
    };
  };
};

const APP_ROW_ORDER = [
  'delivery_income',
  'referral_discounts',
  'order_count',
  'cancelled_order_count',
  'extra_income',
  'documentation_fee',
  'vat',
  'payment_charges',
];

const COUNT_ROW_KEYS = new Set(['order_count', 'cancelled_order_count']);

const BANK_INCOME_ROW_ORDER = ['customer_payment_received', 'other', 'uncategorised'];

const BANK_EXPENSE_ROW_ORDER = [
  'operations',
  'supplies',
  'courier_customs_expenses',
  'border_tax',
  'domains_webhosting',
  'software',
  'marketing',
  'bank_charges',
  'refund',
  'rent',
  'salaries',
  'customer_order_purchase',
  'other',
  'uncategorised',
];

const PROFIT_ROW_ORDER = ['profit', 'profit_pct', 'net_with_investment'];

const DEFAULT_MONTH_SPAN = 6;
const MAX_MONTH_SPAN = 12;

const toMonthString = (date: Date): string =>
  `${date.getFullYear()}-${String(date.getMonth() + 1).padStart(2, '0')}`;

const addMonths = (yyyymm: string, delta: number): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  return toMonthString(new Date(year, month - 1 + delta, 1));
};

const defaultRange = () => {
  const now = new Date();
  const to = toMonthString(now);
  const from = addMonths(to, -(DEFAULT_MONTH_SPAN - 1));
  return { from, to };
};

const formatMonthLabel = (yyyymm: string): string => {
  const [year, month] = yyyymm.split('-').map(Number);
  if (!year || !month) return yyyymm;
  const date = new Date(year, month - 1, 1);
  return date.toLocaleString('default', { month: 'short', year: '2-digit' });
};

const formatValue = (value: number, kind: 'money' | 'count' | 'percent'): string => {
  if (kind === 'count') return String(value);
  if (kind === 'percent') return `${value.toFixed(1)}%`;
  return formatCurrency(value);
};

const CELL_CLASS = 'px-4 py-2 text-right text-sm text-gray-900 tabular-nums whitespace-nowrap';
const LABEL_CLASS = 'sticky left-0 z-10 bg-white px-4 py-2 text-sm font-medium text-gray-800 whitespace-nowrap border-r border-gray-200';
const SUB_SECTION_LABEL_CLASS = 'sticky left-0 z-10 bg-gray-50 px-4 py-2 text-xs font-semibold uppercase tracking-wide text-gray-500 border-r border-gray-200';

// Row/section builders that push <tr> nodes into `sink`. Kept at module scope so
// the section memos depend only on their data (matrix, months).
const makeBuilder = (sink: React.ReactNode[], months: string[]) => {
  const pushSection = (label: string) => {
    sink.push(
      <tr key={`section-${label}`} className="bg-gray-50">
        <td className={SUB_SECTION_LABEL_CLASS}>{label}</td>
        {months.map((m) => (
          <td key={`section-${label}-${m}`} className="bg-gray-50" />
        ))}
      </tr>,
    );
  };

  const pushRow = (
    key: string,
    row: MatrixRow,
    kind: 'money' | 'count' | 'percent',
    options?: { bold?: boolean; colored?: boolean },
  ) => {
    const bold = options?.bold ?? false;
    const colored = options?.colored ?? false;
    sink.push(
      <tr key={key} className={bold ? 'bg-gray-50 font-semibold' : 'hover:bg-gray-50'}>
        <td className={`${LABEL_CLASS}${bold ? ' font-semibold bg-gray-50' : ''}`}>{row.label}</td>
        {row.values.map((value, idx) => {
          let colorClass = '';
          if (colored) {
            if (value > 0) colorClass = ' text-green-700';
            else if (value < 0) colorClass = ' text-red-600';
          }
          return (
            <td key={`${key}-${months[idx]}`} className={`${CELL_CLASS}${colorClass}`}>
              {formatValue(value, kind)}
            </td>
          );
        })}
      </tr>,
    );
  };

  return { pushSection, pushRow };
};

const FinanceMonthlyMatrix: React.FC = () => {
  const [searchParams, setSearchParams] = useSearchParams();
  const { from: defaultFrom, to: defaultTo } = defaultRange();
  const [from, setFrom] = useState<string>(searchParams.get('from') || defaultFrom);
  const [to, setTo] = useState<string>(searchParams.get('to') || defaultTo);
  const [matrix, setMatrix] = useState<MatrixResponse | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [error, setError] = useState<string | null>(null);

  useEffect(() => {
    setSearchParams(prev => {
      prev.set('from', from);
      prev.set('to', to);
      return prev;
    }, { replace: true });
  }, [from, to, setSearchParams]);

  useEffect(() => {
    const load = async () => {
      setIsLoading(true);
      setError(null);
      try {
        const query = new URLSearchParams({ from, to });
        const res = await fetchWithAuth(`/finance_monthly_matrix?${query.toString()}`);
        if (!res.ok) {
          throw new Error(`Request failed with status ${res.status}`);
        }
        const data: MatrixResponse = await res.json();
        setMatrix(data);
      } catch (err) {
        console.error('Error loading finance monthly matrix:', err);
        setError('Failed to load the monthly finance matrix.');
        setMatrix(null);
      } finally {
        setIsLoading(false);
      }
    };

    load();
  }, [from, to]);

  const months = useMemo(() => matrix?.months ?? [], [matrix]);

  const appRows = useMemo(() => {
    if (!matrix) return [] as React.ReactNode[];
    const rows: React.ReactNode[] = [];
    const { pushRow } = makeBuilder(rows, months);
    const { app } = matrix.rows;

    APP_ROW_ORDER.forEach((key) => {
      const row = app[key];
      if (!row) return;
      pushRow(`app-${key}`, row, COUNT_ROW_KEYS.has(key) ? 'count' : 'money');
    });
    if (app.order_items_total) pushRow('app-order-items-total', app.order_items_total, 'money');
    if (app.order_items_total_cost_price) pushRow('app-order-items-cost', app.order_items_total_cost_price, 'money');
    if (app.discounts) pushRow('app-discounts', app.discounts, 'money');
    if (app.refunds) pushRow('app-refunds', app.refunds, 'money');
    if (app.total) pushRow('app-total', app.total, 'money', { bold: true });

    return rows;
  }, [matrix, months]);

  const bankRows = useMemo(() => {
    if (!matrix) return [] as React.ReactNode[];
    const rows: React.ReactNode[] = [];
    const { pushSection, pushRow } = makeBuilder(rows, months);
    const { bank } = matrix.rows;

    pushSection('Income');
    BANK_INCOME_ROW_ORDER.forEach((key) => {
      const row = bank.income[key];
      if (!row) return;
      pushRow(`bank-income-${key}`, row, 'money');
    });
    if (bank.income.total) pushRow('bank-income-total', bank.income.total, 'money', { bold: true });

    pushSection('Investment');
    pushRow('bank-investment', bank.investment, 'money');

    pushSection('Expenses');
    BANK_EXPENSE_ROW_ORDER.forEach((key) => {
      const row = bank.expenses[key];
      if (!row) return;
      pushRow(`bank-expense-${key}`, row, 'money');
    });
    if (bank.expenses.total) pushRow('bank-expense-total', bank.expenses.total, 'money', { bold: true });

    pushSection('Profit');
    PROFIT_ROW_ORDER.forEach((key) => {
      const row = bank.profit[key];
      if (!row) return;
      const kind: 'money' | 'percent' = key === 'profit_pct' ? 'percent' : 'money';
      pushRow(`bank-profit-${key}`, row, kind, { colored: true });
    });

    return rows;
  }, [matrix, months]);

  const renderTable = (title: string, bodyRows: React.ReactNode[]) => (
    <div>
      <h2 className="text-lg font-semibold text-gray-900 mb-2">{title}</h2>
      <div className="bg-white border border-gray-200 rounded-lg shadow-sm overflow-x-auto">
        <table className="min-w-full border-collapse">
          <thead className="bg-gray-50">
            <tr>
              <th className="sticky left-0 z-20 bg-gray-50 px-4 py-2 text-left text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-r border-gray-200">
                Row
              </th>
              {months.map((m) => (
                <th
                  key={`${title}-th-${m}`}
                  className="px-4 py-2 text-right text-xs font-semibold uppercase tracking-wide text-gray-600 border-b border-gray-200 whitespace-nowrap"
                >
                  {formatMonthLabel(m)}
                </th>
              ))}
            </tr>
          </thead>
          <tbody>{bodyRows}</tbody>
        </table>
      </div>
    </div>
  );

  return (
    <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8 py-8">
      <div className="mb-6">
        <h1 className="text-2xl font-bold text-gray-900 mb-2">Monthly Finance</h1>
        <p className="text-gray-600">Month-by-month figures split into App (order data) and Bank Account (external transactions).</p>
      </div>

      <div className="bg-white p-4 rounded-lg shadow-sm border border-gray-200 mb-6">
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-4 items-end">
          <div>
            <label htmlFor="from" className="block text-sm font-medium text-gray-700 mb-1">From</label>
            <input
              type="month"
              id="from"
              value={from}
              min={addMonths(to, -(MAX_MONTH_SPAN - 1))}
              max={to}
              onChange={(e) => setFrom(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <label htmlFor="to" className="block text-sm font-medium text-gray-700 mb-1">To</label>
            <input
              type="month"
              id="to"
              value={to}
              min={from}
              max={toMonthString(new Date())}
              onChange={(e) => setTo(e.target.value)}
              className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
            />
          </div>
          <div>
            <button
              type="button"
              onClick={() => {
                const range = defaultRange();
                setFrom(range.from);
                setTo(range.to);
              }}
              className="px-4 py-2 text-sm font-medium text-gray-700 border border-gray-300 rounded-md hover:bg-gray-50"
            >
              Reset to last {DEFAULT_MONTH_SPAN} months
            </button>
          </div>
        </div>
        <p className="text-xs text-gray-500 mt-2">Maximum range: {MAX_MONTH_SPAN} months.</p>
      </div>

      {error && (
        <div className="mb-4 px-4 py-3 bg-red-50 border border-red-200 text-red-800 rounded">
          {error}
        </div>
      )}

      {isLoading && !matrix && (
        <div className="text-gray-600">Loading…</div>
      )}

      {matrix && (
        <div className={`space-y-8 transition-opacity ${isLoading ? 'opacity-60' : ''}`}>
          {renderTable('App', appRows)}
          {renderTable('Bank Account', bankRows)}
        </div>
      )}
    </div>
  );
};

export default FinanceMonthlyMatrix;
